import {
  BarChartOutlined,
  Construction,
  CopyAll,
  Delete,
  Output,
  Share,
} from "@mui/icons-material";

import type { AudienceDialogs, AudienceWorkflow } from "@/hooks";
import { ActionButton } from "@/types";
import { AudiencePageState } from "./audiencePageState";

export type AudiencePageActionId = 'delete' | 'share' | 'unshare' | 'clone' | 'build' | 'insight' | 'export';

/**
 * The action buttons for an audience detail page, in display order, limited
 * to those the page includes.
 */
export const audiencePageActions = (
  pageState: AudiencePageState,
  workflow: Pick<AudienceWorkflow, 'build' | 'clone' | 'openInsight' | 'openExport' | 'isRunning'>,
  dialogs: Pick<AudienceDialogs, 'confirmDelete' | 'confirmShare' | 'confirmUnshare'>,
  include: AudiencePageActionId[]
): ActionButton[] => {
  const { can, primary, isShared, isBuilt, insightIsStale } = pageState;

  const actions: Record<AudiencePageActionId, ActionButton> = {
    delete: {
      id: 'delete',
      label: 'Delete',
      icon: Delete,
      onClick: dialogs.confirmDelete,
      disabled: !can.delete,
      color: 'error',
    },
    share: {
      id: 'share',
      label: isShared ? 'Reshare' : 'Share',
      icon: Share,
      onClick: dialogs.confirmShare,
      disabled: !can.share,
    },
    unshare: {
      id: 'unshare',
      label: 'Unshare',
      icon: Share,
      color: 'error',
      onClick: dialogs.confirmUnshare,
      disabled: !can.unshare,
      hidden: !isShared,
    },
    clone: {
      id: 'clone',
      label: 'Clone',
      icon: CopyAll,
      onClick: workflow.clone,
      disabled: !can.clone,
      hidden: !isShared,
    },
    build: {
      id: 'build',
      label: isBuilt ? 'Rebuild' : 'Build',
      icon: Construction,
      onClick: workflow.build,
      loading: workflow.isRunning('build'),
      disabled: !can.build,
      isPrimary: primary.build,
    },
    insight: {
      id: 'insight',
      label: 'Insight',
      icon: BarChartOutlined,
      onClick: () => workflow.openInsight(insightIsStale),
      loading: workflow.isRunning('insight'),
      disabled: !can.insight,
      isPrimary: primary.insight,
    },
    export: {
      id: 'export',
      label: 'Export',
      icon: Output,
      onClick: workflow.openExport,
      disabled: !can.export,
      isPrimary: primary.export,
    },
  };

  const order: AudiencePageActionId[] = ['delete', 'share', 'unshare', 'clone', 'build', 'insight', 'export'];
  return order.filter((id) => include.includes(id)).map((id) => actions[id]);
}
